import { FormMessageTypes } from "@/models/enums";
import { FormResponse } from "@/models/forms";
import { formatAmount, formatDate } from "@/util";
import { FC } from "react";
import { useTranslation } from "react-i18next";

interface IFormsGroupOrderViewProps {
    formData: FormResponse | null;
}

const FormsGroupOrderModalContent: FC<IFormsGroupOrderViewProps> = (props: IFormsGroupOrderViewProps): JSX.Element => {
    const { t } = useTranslation();

    function getMessageTypeData(messageType: FormMessageTypes | null | undefined) {
        if (messageType?.toString() === FormMessageTypes[FormMessageTypes.MessageOne]) {
            return t("forms.CreateGroupOrder.MessageTextFirst");
        } else if (messageType?.toString() === FormMessageTypes[FormMessageTypes.MessageTwo]) {
            return t("forms.CreateGroupOrder.MessageTextSecond");
        } else {
            return t("forms.CreateGroupOrder.MessageTextThird");
        }
    }

    function getAlternativeHolidayDatesInfo(dateOne: string, dateTwo: string, dateThree: string): string {
        var datesArray = [dateOne, dateTwo, dateThree];
        var filteredDates = datesArray
            .filter((date) => date !== "")
            .map((date) => formatDate(date))
            .join(", ");

        return filteredDates;
    }

    function getGroupOrderEnumsData(enumTypes: string): string {
        const typesArray = enumTypes
            .split(", ")
            .map((type) => t(`forms.CreateGroupOrder.${type}`))
            .join(", ");

        return typesArray;
    }

    function handleFormYesOrNoProp(property: boolean | undefined) {
        if (property) {
            return t("forms.Yes");
        } else {
            return t("forms.No");
        }
    }

    function getRegionsInfo(
        regionOne: string | undefined,
        regionTwo: string | undefined,
        regionThree: string | undefined
    ): string {
        const regionDataArray = [regionOne, regionTwo, regionThree];
        const regionsArray = regionDataArray.filter((region) => region !== "").join(", ");

        return regionsArray;
    }

    return (
        <div style={{ display: "grid", gap: "20px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div style={{ padding: "13px 10px 14px 15px" }}>
                            <div className="font-style">{t("forms.GroupOrder.Message")}</div>
                            <div style={{ paddingTop: 7 }} className="font-style-bold-16">
                                {getMessageTypeData(props.formData?.messageType)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.FunDay.AlternativeHolidayDates")}</div>
                            <div className="font-style-bold-16">
                                {getAlternativeHolidayDatesInfo(
                                    props.formData?.alternativeHolidayDateOne ?? "",
                                    props.formData?.alternativeHolidayDateTwo ?? "",
                                    props.formData?.alternativeHolidayDateThree ?? ""
                                )}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.alternativeHolidayDateComments}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.ClientName")}</div>
                            <div className="font-style-bold-16">{props.formData?.clientName}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Mobile")}</div>
                            <div className="font-style-bold-16">{props.formData?.mobile}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Email")}</div>
                            <div className="font-style-bold-16">{props.formData?.email}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.PurposeVacation")}</div>
                            <div className="font-style-bold-16">{props.formData?.formGoal}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.NumberRoomsDesired")}</div>
                            <div className="font-style-bold-16">
                                {props.formData?.numberRoomsDesired?.toLocaleString("en-US")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.DesiredRegions")}</div>
                            <div className="font-style-bold-16">
                                {getRegionsInfo(
                                    props.formData?.desiredRegionOne ?? "",
                                    props.formData?.desiredRegionTwo ?? "",
                                    props.formData?.desiredRegionThree ?? ""
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.BudgetEmployee")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.budgetPerEmployee, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.FamilyBudget")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.familyBudget, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.BudgetCouple")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.budgetForCouple, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.budgetComments}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.StayingRequest")}</div>
                            <div className="font-style-bold-16">
                                {getGroupOrderEnumsData(props.formData?.stayingRequestType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.stayingRequestComments}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.HotelLevel")}</div>
                            <div className="font-style-bold-16">
                                {getGroupOrderEnumsData(props.formData?.hotelLevelType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Visitors")}</div>
                            <div className="font-style-bold-16">
                                {t(`forms.CreateGroupOrder.${props.formData?.visitors}`)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.NeedBus")}</div>
                            <div className="font-style-bold-16">{handleFormYesOrNoProp(props.formData?.needBus)}</div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.NeedGuide")}</div>
                            <div className="font-style-bold-16">{handleFormYesOrNoProp(props.formData?.needBus)}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Boardbase")}</div>
                            <div className="font-style-bold-16">
                                {getGroupOrderEnumsData(props.formData?.mealType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.mealComments}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div style={{ padding: "13px 10px 14px 15px" }}>
                            <div className="font-style">{t("forms.GroupOrder.IncorporateLecture")}</div>
                            <div style={{ paddingTop: 7 }} className="font-style-bold-16">
                                {handleFormYesOrNoProp(props.formData?.wantIncorporateLectureRecognition)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Addition")}</div>
                            <div className="font-style-bold-16">
                                {getGroupOrderEnumsData(props.formData?.specialRequestStayType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.BudgetEveningArtist")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.eveningArtistBudget ?? 0, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.GeneralBudget")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.generalBudget ?? 0, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Description")}</div>
                            <div className="font-style-bold-16">{t("forms.CreateGroupOrder.DescriptionText")}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.InterestedInTrip")}</div>
                            <div className="font-style-bold-16">
                                {handleFormYesOrNoProp(props.formData?.interestedTrip ?? false)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Budget")}</div>
                            <div className="font-style-bold-16">
                                {formatAmount(props.formData?.interestedTripBudget ?? 0, props.formData?.currencyId)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.interestedTripComments}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div style={{ padding: "13px 10px 14px 15px" }}>
                            <div className="font-style">{t("forms.GroupOrder.AdditionDrinks")}</div>
                            <div style={{ paddingTop: 7 }} className="font-style-bold-16">
                                {handleFormYesOrNoProp(props.formData?.additionDrinksOnEvent ?? false)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.Addition")}</div>
                            <div className="font-style-bold-16">
                                {getGroupOrderEnumsData(props.formData?.additionDrinksType?.toString() ?? "")}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.additionDrinksComments}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.ArtistPerformance")}</div>
                            <div className="font-style-bold-16">
                                {t(`forms.CreateGroupOrder.${props.formData?.artistPerformanceType}`)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.artistPerformanceComments}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", paddingBottom: "40px" }}>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.AdditionCoffeeCake")}</div>
                            <div className="font-style-bold-16">
                                {handleFormYesOrNoProp(props.formData?.additionCoffeeCake ?? false)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.additionCoffeeCakeComments}</div>
                        </div>
                    </div>
                </div>
                <div className="forms-view-content-data">
                    <div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.GroupOrder.OtherEventsNeeded")}</div>
                            <div className="font-style-bold-16">
                                {handleFormYesOrNoProp(props.formData?.otherEventsNeeded ?? false)}
                            </div>
                        </div>
                        <div className="forms-view-divider"></div>
                        <div className="forms-view-data-row">
                            <div className="font-style">{t("forms.Comments")}</div>
                            <div className="font-style-bold-16">{props.formData?.otherEventsNeededComments}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormsGroupOrderModalContent;
